import {
  FocusEventHandler,
  ForwardedRef,
  forwardRef,
  ReactNode,
  RefObject,
  useRef,
  WheelEventHandler,
} from 'react';

import { colors } from 'ui/theme/colors';
import { CircleSpinner } from 'ui/spinner';

import { INPUT_TEXT_TYPES } from '../constants';
import { ErrorTheme } from '../types';
import { Positioner, StyledInput, StyledNegativeFill, StyledWrapper } from '../InputStyles';

export type InputTextProps = {
  /** optional input name */
  name?: string;
  /** optional className for attaching styles */
  className?: string;
  /** optional override for data-test tag */
  'data-test'?: string;
  /** if the form field is currently in an error state */
  hasError?: boolean;
  /** handles if the icon is rendered or not */
  hasIcon?: boolean;
  /** object to hold the color values of an error or warning */
  errorTheme?: ErrorTheme;
  /** a unique identifier */
  id?: string;
  /** if the input is non-editable */
  isReadOnly?: boolean;
  /** if the input is disabled */
  isDisabled?: boolean;
  /** if the input is required */
  isRequired?: boolean;
  /** A passed-in ref for the inner input element */
  inputRef?: RefObject<HTMLInputElement> | null;
  /** A passed-in ref for the input wrapper element that includes handlebars and other wrapped components */
  inputWrapperRef?: RefObject<HTMLDivElement> | null;
  /** The maximum value to accept for this input */
  maxValue?: number;
  /** The maximum amount of characters allowed */
  maxLength?: number;
  /** The minimum value to accept for this input */
  minValue?: number;
  /** the function to run when the input is updated */
  onChange?: (value: string) => void;
  /** the function to run when the input loses focus */
  onBlur?: FocusEventHandler<HTMLInputElement> | (() => void);
  /** the function to run when the input is in focus */
  onFocus?: FocusEventHandler<HTMLInputElement> | (() => void);
  /** the function will run when the user scrolls inside the input */
  onWheel?: WheelEventHandler<HTMLInputElement> | (() => void);
  /** The placeholder text in the input */
  placeholder?: string;
  /** Additional text or a React Node to display before the value */
  Prefix?: ReactNode;
  /** HTML size attribute for input */
  size?: number;
  /** The number that specifies the granularity that the value must adhere to */
  step?: number;
  /** Additional text or a React Node to display after the value */
  Suffix?: ReactNode;
  /** the type of the text input */
  type?: 'email' | 'password' | 'text' | 'url' | 'number' | 'search' | 'time' | 'date' | 'tel';
  /** The value of the input */
  value: string;
  /** Whether or not the input should display autocomplete */
  autoComplete?: string;
  /** Whether or not the input should automatically be focused */
  autoFocus?: boolean;
  /** Value to customize width of input, can be percentage or pixel value */
  width?: string;
  onKeyDown?: (e: any) => void;
  /** optional loading indicator for when options are not yet loaded */
  isLoading?: boolean;
  /** Applies sticky positioning. Used only for FilterSelect, where the input
   * is pinned to the top of a list of options. */
  applyStickyStyles?: boolean;
};

const emptyMethod = () => {
  // empty
};

export const InputText = forwardRef(
  (
    {
      autoFocus = false,
      autoComplete,
      className,
      hasError = false,
      hasIcon = true,
      errorTheme = {
        errorColor: colors.red100,
        Icon: StyledNegativeFill,
      },
      id = '',
      inputRef = null,
      inputWrapperRef = null,
      maxValue,
      minValue,
      onWheel = emptyMethod,
      onBlur = emptyMethod,
      onChange = emptyMethod,
      onFocus = emptyMethod,
      placeholder = '',
      Prefix = null,
      isReadOnly = false,
      isDisabled = false,
      isRequired = false,
      Suffix = null,
      type = INPUT_TEXT_TYPES.TEXT,
      value = '',
      width,
      isLoading = false,
      applyStickyStyles = false,
      ...props
    }: InputTextProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    /**
    Use the passed-in input ref if one exists, otherwise use a local one we create.
    This component should be refactored to use forwardRef when migrating it to TypeScript.
   */
    const localRef = useRef<HTMLInputElement>(null);

    const inputEl = inputRef || localRef || ref;
    /**
   Update 8/4/2022 we find 'select all' behavior frustrating. Instead of 'select all',
   we want to place the cursor at the end of the text.

   NOTE: Storybook documentation props currently breaks when there is optional
    chaining in the same file as the props.  Until this is fixed, we
    unfortunately have to do the below :/
   */
    const handleAutoComplete = () => {
      inputEl?.current?.focus();
      if (autoComplete?.length) {
        /** there is a bug in react that causes autocomplete to not be set. see: https://github.com/facebook/react/issues/15739
         *  so we have to set it manually ourselves if it is a desired thing.
         */
        inputEl?.current?.setAttribute('autocomplete', autoComplete);
      }
    };

    const showSuffix = !hasError && (isLoading || Suffix);
    const isEditable = !isReadOnly && !isDisabled;
    const { errorColor, Icon } = errorTheme;

    return (
      <StyledWrapper
        ref={inputWrapperRef}
        data-test={props['data-test'] ? `${props['data-test']}-wrapper` : 'input-wrapper'}
        data-error={hasError}
        hasError={hasError}
        errorTheme={errorTheme}
        isReadOnly={!isEditable}
        className={className}
        width={width}
        data-sticky={applyStickyStyles}
      >
        {Prefix && (
          <Positioner color={colors.textPrimary} data-test="input-prefix" className="input-prefix">
            {Prefix}
          </Positioner>
        )}

        <StyledInput
          aria-required={isRequired}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          data-test="input-text"
          data-error={hasError}
          id={id}
          max={maxValue}
          min={minValue}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => {
            onFocus(e);
            handleAutoComplete();
          }}
          onBlur={onBlur}
          onWheel={(e) => {
            onWheel(e);

            /** Disable number increment/decrement on user scroll inside input box */
            if (type === INPUT_TEXT_TYPES.NUMBER) {
              e.currentTarget.blur();
            }
          }}
          placeholder={placeholder}
          readOnly={isReadOnly}
          disabled={isDisabled}
          ref={inputEl}
          required={isRequired}
          tabIndex={!isEditable ? -1 : 0} // ignore tabbing when in read-only mode
          type={type}
          value={value ?? ''}
          {...props}
        />

        {showSuffix && (
          <Positioner color={colors.grey60} data-test="input-suffix">
            {isLoading ? <CircleSpinner size={16} color={colors.textSecondary} /> : Suffix}
          </Positioner>
        )}

        {hasError && hasIcon && (
          <Positioner data-test="error-icon">
            <Icon color={errorColor} />
          </Positioner>
        )}
      </StyledWrapper>
    );
  },
);
